// Third-party
import React from 'react';

// Components
import DefaultModal from './DefaultModal';
import SimpleTable from '../tables/SimpleTable';
import { DisclaimerTooltip, Tooltip } from '../tooltips';
import DisclaimerFooter from '../disclaimers/DisclaimerFooter';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString } from '../../../utils/i18n';
import { DISCLAIMER_KEYS } from '../../../utils/disclaimers/constants';
import { uniqueArray } from '../../../utils/objects';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import BasicBenchmark from '@/types/basic-benchmark';
import ComparisonHolding from '@/types/comparison-holding';

type Props = {
  activeBenchmark?: ComparisonItem;
  item: ComparisonItem;
  show: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const renderTableData = (id: string, nameOption: 'short' | 'full', benchmarks?: Dictionary<BasicBenchmark>) => {
  if (benchmarks && benchmarks[id]) {
    const name = formatLocalizedString(benchmarks[id].names[nameOption]);
    return (
      <Tooltip message={name} delayShow={300}>
        <span>{name}</span>
      </Tooltip>
    );
  }

  return <DisclaimerTooltip showLabel disclaimer={{ key: DISCLAIMER_KEYS.HOLDING_MISSING_BENCHMARK }} />;
};

const getDisclaimers = (holdings: ComparisonHolding[]) => {
  const types = uniqueArray(holdings.map(h => h.type));
  const disclaimers = [];
  for (const type of types) {
    if (type === 'stock') {
      disclaimers.push(formatMessage('Disclaimers.StockBenchmarksProvider'));
    }
    if (type === 'customYield') {
      disclaimers.push(formatMessage('Disclaimers.GIC/HISABenchmarksProvider'));
    }
    if (type === 'fund') {
      disclaimers.push(formatMessage('Disclaimers.FundBenchmarksProvider'));
    }
  }
  return disclaimers;
};

const renderBenchmarks = (
  holdings: ComparisonHolding[],
  benchmarks?: Dictionary<BasicBenchmark>,
  riskFree?: Dictionary<BasicBenchmark>,
  activeBenchmark?: ComparisonItem,
) => {
  const TABLE_HEADERS = [
    <FormattedMessage key="BenchmarksModal.Holding" id="BenchmarksModal.Holding" />,
    <FormattedMessage key="BenchmarksModal.Benchmark" id="BenchmarksModal.Benchmark" />,
    <FormattedMessage key="BenchmarksModal.RiskFree" id="BenchmarksModal.RiskFree" />,
  ];

  const rows = holdings.map(h => {
    const name = h.name ? formatLocalizedString(h.name) : undefined;

    return {
      elements: [
        <Tooltip key={name} message={name} delayShow={300}>
          <span>{name}</span>
        </Tooltip>,
        activeBenchmark ? (
          <Tooltip message={formatLocalizedString(activeBenchmark.name)} delayShow={300}>
            <span>{formatLocalizedString(activeBenchmark.name)}</span>
          </Tooltip>
        ) : (
          renderTableData(h.id, 'full', benchmarks)
        ),
        renderTableData(h.id, 'short', riskFree),
      ],
    };
  });

  return (
    <>
      <SimpleTable
        className="stripped"
        stickyHeaders
        data={{
          headers: TABLE_HEADERS,
          rows,
        }}
      />
      <DisclaimerFooter disclaimers={getDisclaimers(holdings)} />
    </>
  );
};

const BenchmarksModal = (props: Props) => {
  const {
    activeBenchmark,
    item: { holdings, benchmarks, name },
    onCancel,
    show,
  } = props;
  const modalBody = renderBenchmarks(holdings, benchmarks && benchmarks.market, benchmarks && benchmarks.riskFree, activeBenchmark);
  const itemName = formatLocalizedString(name);

  return (
    <div onClick={e => e.stopPropagation()}>
      <DefaultModal
        className="benchmarks-modal"
        title={formatMessage('BenchmarksModal.Title', { name: itemName })}
        body={modalBody}
        show={show}
        secondaryActionLabel={formatMessage('Common.Close')}
        onSecondaryAction={onCancel}
        onCancel={onCancel}
        hidePrimaryButton
        hideCancelButton
      />
    </div>
  );
};

export default BenchmarksModal;
